import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Mail, MessageSquare, User, Sparkles, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "@/lib/api";

export default function ContactForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Pull UTM params from the current URL so we can attribute the
      // lead to a specific marketing channel inside Brevo + GA4.
      const usp = new URLSearchParams(window.location.search);
      await api.post("/forms/contact", {
        ...form,
        source: "contact",
        page_url: window.location.pathname,
        utm_source:   usp.get("utm_source")   || undefined,
        utm_medium:   usp.get("utm_medium")   || undefined,
        utm_campaign: usp.get("utm_campaign") || undefined,
      });
      toast.success(t("commonToasts.contactReceived", "Message received · we'll respond within a few hours."));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("common.submissionFailed", "Submission failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-14 lg:py-28 overflow-hidden">

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left: Pitch */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5 space-y-6"
          >
            <div className="pill-mono inline-flex">{t("contact.pill")}</div>
            <h2 className="font-display text-3xl lg:text-5xl font-medium leading-[1.05] tracking-tight">
              {t("contact.title")} <span className="text-gradient-brand font-semibold">{t("contact.titleAccent")}</span>
            </h2>
            <p className="text-[color:var(--text-2)] text-sm lg:text-base font-light leading-relaxed">
              {t("contact.subtitle")}
            </p>

            <div className="space-y-3">
              {[
                { icon: Sparkles, label: t("contact.fast"), desc: t("contact.fastDesc") },
                { icon: ShieldCheck, label: t("contact.kyc"), desc: t("contact.kycDesc") },
                { icon: Mail, label: t("contact.multi"), desc: t("contact.multiDesc") },
              ].map((it) => (
                <div key={it.label} className="flex items-start gap-3">
                  <div className="w-12 h-12 shrink-0 rounded-lg bg-brand/10 border border-brand/30 grid place-items-center">
                    <it.icon size={15} className="text-brand" />
                  </div>
                  <div>
                    <div className="font-display text-sm font-medium text-[color:var(--text-1)]">{it.label}</div>
                    <div className="text-[color:var(--text-3)] text-xs">{it.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={submit}
              className="card-beam p-7 lg:p-9 relative overflow-hidden"
              data-testid="contact-form"
              style={{
                background:
                  "linear-gradient(180deg, var(--surface) 0%, var(--surface-2) 100%)",
              }}
            >
              <div className="absolute -top-20 -right-20 w-48 h-48 bg-brand/15 blur-3xl rounded-full pointer-events-none" />

              <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div className="field-wrap">
                  <input
                    data-testid="contact-name"
                    required
                    placeholder={t("contact.name")}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className="field-wrap">
                  <input
                    data-testid="contact-email"
                    required
                    type="email"
                    placeholder={t("contact.email")}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="field-wrap mb-4">
                <input
                  data-testid="contact-subject"
                  placeholder={t("contact.subject")}
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                />
              </div>

              <div className="field-wrap mb-5">
                <textarea
                  data-testid="contact-message"
                  required
                  rows={5}
                  placeholder={t("contact.message")}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 text-[color:var(--text-3)] text-xs">
                  <MessageSquare size={13} className="text-brand" />
                  <span className="font-num">{form.message.length}</span>
                  <span>/ 2000 {t("contact.characters")}</span>
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="btn-solid-brand text-sm disabled:opacity-60"
                >
                  <Send size={14} /> {submitting ? t("contact.sending") : t("contact.send")}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
